var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import { isPlatformBrowser } from '@angular/common';
import { Auth } from '@freelancer/auth';
import { Applications, CompatApplications, Environment, } from '@freelancer/config';
import * as Sentry from '@sentry/browser';
import * as SentryIntegrations from '@sentry/integrations';
import { map } from 'rxjs/operators';
import * as UAParser from 'ua-parser-js';
var ErrorTracking = /** @class */ (function () {
    function ErrorTracking(auth, config, appName, environment, platformId, disableErrorTracking) {
        var _this = this;
        this.auth = auth;
        this.config = config;
        this.appName = appName;
        this.environment = environment;
        if (isPlatformBrowser(platformId) &&
            this.config.enableErrorTracking &&
            this.isCurrentBrowser() &&
            !disableErrorTracking) {
            Sentry.init({
                dsn: this.config.sentryDsn,
                release: this.config.gitRevision,
                environment: this.environment,
                ignoreErrors: [
                    // This is caused by anchorScrolling, see
                    // https://github.com/angular/angular/issues/26854.
                    // It's harmless though.
                    /SyntaxError: Failed to execute 'querySelector' on 'Document': '.*' is not a valid selector/,
                    /SyntaxError: '.*' is not a valid selector/,
                    /SyntaxError: SyntaxError/,
                    /SyntaxError: The string did not match the expected pattern/,
                    // These are caused browsers cancelling ongoing requests due to a
                    // location change
                    'Http failure response for (unknown url): 0',
                    /Error: Loading chunk \d+ failed/,
                    'NS_ERROR_NOT_INITIALIZED',
                    // Random client errors we can't do much about but show a banner at
                    // some point
                    'Out of memory',
                    // The Angular Router currently throws on URLs with scripts tags or
                    // invalid characters
                    // See https://github.com/angular/angular/issues/21032
                    'Error: Cannot match any routes. URL Segment:',
                    // This is likely caused by some broken Kaspersky security product,
                    // which wraps `XMLHttpRequest` in order to analyze web pages
                    // behaviors, hence why the Error ends up t in the Angular Zone.
                    'ns.GetCommandSrc is not a function',
                    // FIXME: agm-core is broken as the Maps SDK isn't always loaded from
                    // outside the Angular Zone
                    // (https://github.com/SebastianM/angular-google-maps/blob/master/packages/core/services/fit-bounds.ts#L42)
                    // This is caused by clients blocking the Google Maps SDK
                    'Event: {"isTrusted":true}',
                    // This is suspected to be caused by some broken Chrome Desktop
                    // browser extension, see T121550
                    'target parent not found',
                ],
                whitelistUrls: [/\/assets\//],
                blacklistUrls: [/^file:\/\//, /^moz-extension:\/\//],
                integrations: function (integrations) {
                    // integrations will be all default integrations
                    return __spreadArrays(integrations.filter(function (integration) {
                        return !['TryCatch', 'GlobalHandlers'].includes(integration.name);
                    }), [
                        new SentryIntegrations.Dedupe(),
                        new SentryIntegrations.ExtraErrorData(),
                        new SentryIntegrations.ReportingObserver(),
                    ]);
                },
                beforeSend: function (event) {
                    if (event.message && event.message.startsWith('ReportingObserver')) {
                        // There's a Boomerang bug where getEntriesByType('navigation')
                        // (Nav Timings 2) is called before it's ready, leading Boomerang
                        // to fallback to chrome.loadTimes()
                        if (event.message.includes('chrome.loadTimes() is deprecated')) {
                            return null;
                        }
                        // This is caused by perfect-scrolling, remove when/if
                        // https://github.com/utatti/perfect-scrollbar/pull/810 ever get
                        // merged.
                        if (event.message.includes('Ignored attempt to cancel a touchmove event')) {
                            return null;
                        }
                        // Only include events generated from the webapp source files in
                        // order to cut off the noise
                        if (event.extra &&
                            event.extra.body &&
                            event.extra.body.sourceFile &&
                            event.extra.body.sourceFile.includes('/assets/webapp/')) {
                            return event;
                        }
                        return null;
                    }
                    return event;
                },
            });
            Sentry.configureScope(function (scope) {
                scope.setTag('releaseDate', new Date(_this.config.buildTimestamp).toUTCString());
                scope.setTag('app', _this.appName);
            });
            this.isSentryEnabled = true;
        }
    }
    ErrorTracking.prototype.captureMessage = function (msg, extras) {
        if (this.isSentryEnabled && this.isCurrentBrowser()) {
            this.auth.authState$
                .pipe(map(function (auth) {
                Sentry.withScope(function (scope) {
                    if (auth) {
                        scope.setUser({ id: auth.userId });
                    }
                    if (extras) {
                        scope.setExtras(extras);
                    }
                    Sentry.captureMessage(msg);
                });
            }))
                .toPromise();
        }
    };
    ErrorTracking.prototype.captureException = function (error, extras) {
        if (this.isSentryEnabled && this.isCurrentBrowser()) {
            this.auth.authState$
                .pipe(map(function (auth) {
                Sentry.withScope(function (scope) {
                    if (auth) {
                        scope.setUser({ id: auth.userId });
                    }
                    if (extras) {
                        scope.setExtras(extras);
                    }
                    Sentry.captureException(error);
                });
            }))
                .toPromise();
        }
    };
    // yeah UA parsing is bad blah blah but catching large failures quickly is
    // more important than being flooded by exotic browsers.
    ErrorTracking.prototype.isCurrentBrowser = function () {
        var ua = new UAParser(window.navigator.userAgent);
        var _a = ua.getBrowser(), name = _a.name, _b = _a.version, version = _b === void 0 ? '' : _b;
        if ((name === 'IE' && parseInt(version, 10) >= 11) ||
            (name === 'Edge' && parseInt(version, 10) >= 17) ||
            (name === 'Firefox' &&
                parseInt(version, 10) >= 65 &&
                !(ua.getOS().name === 'Android')) ||
            (name === 'Chrome' && parseInt(version, 10) >= 72) ||
            (name === 'Chromium' && parseInt(version, 10) >= 72) ||
            (name === 'Safari' && parseInt(version, 10) >= 11)) {
            return true;
        }
        return false;
    };
    return ErrorTracking;
}());
export { ErrorTracking };
